import { Flyer, FlyerItemDetails } from '../definitions/FlyerDefinitions'
import { Coupon } from '../definitions/CouponDefinitions'
import { AnalyticsService } from '../services/AnalyticsService'
import { DisplayType } from '../definitions/ApiDefinitions'
import { i18n } from '../i18n/I18n'
import { Dialog, DialogHeader } from '../components/flipp-dialog/Dialog'
import { ToastContainer } from '../components/flipp-toast-container/ToastContainer'
import { ItemDialog } from '../components/flipp-item-dialog/ItemDialog'
import { Country, LocationService } from '../services/LocationService'
import { ClipFlyerItemButton } from '../components/flipp-clip-button/ClipFlyerItemButton'
import { FlyerItemElement } from '../components/flipp-flyer-item/FlyerItemElement'
import { FlyerItemStructureData } from '../components/flipp-data-structure/FlyerItemStructureData'
import { CouponClippingDialogContent } from '../components/flipp-coupon-clipping/CouponClipping'
import { AppsFlyerService } from '../services/AppsFlyerService'
import { ConfigService } from '../services/ConfigService'
import { AuthenticationService } from '../services/AuthenticationService'
import { icons } from '../icons/Icons'
import { LinkService } from '../services/LinkService'
import { AccountsService } from '../services/AccountsService'
import { EventService } from '../services/EventService'
import { DELETE_ACCOUNT_CONFIRMATION_POPUP_TAG_ELEMENT_ID } from '../components/flipp-info-popup/InfoPopup'

function _makeTTM(item: FlyerItemDetails) {
    const location = LocationService.getInstance()
    return item.ttmUrl
        .replace('__POSTAL_CODE__', location.postalCode)
        .replace('__FLIPP_POSTAL_CODE__', location.postalCode)
        .replace('__FLIPP_LOCALE__', location.locale)
}

function _makeDeepShareLink(url: string, id: string, flyerRunId?: string) {
    const location = LocationService.getInstance()
    const deepShareUrl = new URL(url)
    deepShareUrl.searchParams.append('user_postal_code', location.postalCode)
    deepShareUrl.searchParams.append('flyer_item_id', id)
    deepShareUrl.searchParams.append('pop_item', 'true')
    deepShareUrl.searchParams.append('trigger_cart', 'true')
    if (flyerRunId) {
        deepShareUrl.searchParams.append('flyer_run_id', flyerRunId)
    }
    return deepShareUrl.href
}

function _reviewsClicked() {
    const mql = window.matchMedia('(max-width: 600px)')
    if (mql.matches) return
    const container = document.querySelectorAll('flipp-flyer-item .container')
    const itemInfo = document.querySelectorAll(
        'flipp-dialog.flipp-flyer-item-dialog .item-info'
    )
    const reviews = document.querySelectorAll(
        'flipp-dialog.flipp-flyer-item-dialog section.reviews'
    )
    if (itemInfo.length === 0 || container.length === 0 || reviews.length === 0)
        return
    const paddingTop = (container[0] as HTMLElement).offsetTop
    const offset = (reviews[0] as HTMLElement).offsetTop
    itemInfo[0].scrollTop = 1
    setTimeout(() => {
        itemInfo[0].scrollTop = offset - paddingTop
    }, 0)
}

export function makeItemPop(
    item: FlyerItemDetails,
    flyer: Flyer,
    coupons: Coupon[]
) {
    const title = itemPopTitle(item, flyer)
    const locationService = LocationService.getInstance()
    const config = ConfigService.getInstance()

    let externalLinkButton = null
    if (config.deepShareUrl) {
        const flyerRunId = item.flyerRunId ? item.flyerRunId.toString() : null
        const deepShareLink = _makeDeepShareLink(
            config.deepShareUrl,
            item.id.toString(),
            flyerRunId
        )
        externalLinkButton = (
            <a
                href={deepShareLink}
                class="flipp-button"
                rel="nofollow"
                target="_blank"
            >
                <div>
                    <span class="label">Shop Now</span>
                </div>
            </a>
        )
    } else if (item.ttmUrl) {
        const ttmUrl = _makeTTM(item)
        externalLinkButton = (
            <a
                href={ttmUrl}
                class="flipp-button"
                rel="nofollow"
                target="_blank"
                onClick={() => {
                    AnalyticsService.getInstance().trackFlyerItem(
                        flyer,
                        item.id,
                        1
                    )
                    AppsFlyerService.getInstance().trackFlyerItem()
                }}
            >
                <div>
                    <span class="label">{item.ttmLabel}</span>
                </div>
            </a>
        )
    }

    const getItemLabel = () => {
        switch (item.displayType) {
            case DisplayType.LINK:
                return 'link'
            case DisplayType.IFRAME:
            case DisplayType.ONLYFEATUREHTML:
                return 'iframe'
            case DisplayType.VIDEO:
                return 'video'
            default:
                return 'item'
        }
    }

    const indexed: any = {}
    if (flyer.webIndexed && coupons.length === 0) {
        indexed['data-indexed'] = true
    }

    const unavailable =
        (item.displayType === DisplayType.COUPONV2 ||
            item.displayType === DisplayType.COUPONV3) &&
        coupons.length === 0

    const originalPriceBox = item.originalPrice ? (
        <div class="original-price-info">
            <flipp-price value={item.originalPrice} />
            <span>{i18n('you_save')}</span>
            <flipp-price
                value={`${Number(item.originalPrice) - Number(item.currentPrice)}`}
            />
            <span>
                (
                {i18n('percent_off', {
                    percent: `${Math.floor(
                        ((Number(item.originalPrice) -
                            Number(item.currentPrice)) /
                            Number(item.originalPrice)) *
                            100
                    )}`,
                })}
                )
            </span>
        </div>
    ) : null

    const showPrice = item.prePriceText || item.currentPrice || item.priceText
    const priceBox = showPrice ? (
        <div class="item-price-info">
            <span class="pre-price-text">{item.prePriceText}</span>
            <flipp-price value={item.currentPrice} />
            <span class="price-text">{item.priceText}</span>
        </div>
    ) : null

    return (
        <Dialog
            class="flipp-flyer-item-dialog"
            transient
            id="flipp-flyer-item-dialog"
        >
            <ToastContainer>
                <ItemDialog
                    {...indexed}
                    data-type={getItemLabel()}
                    details={{
                        itemImageUrl: item.imageUrl,
                        merchantLogoUrl: flyer.merchantLogo,
                        unavailable: unavailable
                            ? {
                                  title: i18n('coupon_unavailable_cta'),
                                  message:
                                      locationService.country === Country.CANADA
                                          ? i18n(
                                                'coupon_unavailable_limit_message'
                                            )
                                          : i18n(
                                                'coupon_unavailable_fsa_message'
                                            ),
                              }
                            : null,
                    }}
                >
                    <span content-slot="title">{title}</span>
                    {item.reviewAverageRating &&
                    item.reviewSamples.length > 0 ? (
                        <span content-slot="ratings" flex-horizontal>
                            <button onClick={_reviewsClicked}>
                                <div flex-horizontal>
                                    <flipp-rating
                                        value={`${item.reviewAverageRating}`}
                                    />
                                    <span>
                                        ({`${item.reviewSamples.length}`})
                                    </span>
                                </div>
                            </button>
                        </span>
                    ) : null}
                    {priceBox ? (
                        <span content-slot="subtitle">
                            <div>{priceBox}</div>
                        </span>
                    ) : null}
                    {originalPriceBox ? (
                        <span content-slot="dealDetails">
                            <div class="fade">{originalPriceBox}</div>
                        </span>
                    ) : null}
                    <div content-slot="cta">
                        <div class="cta-buttons" flex-horizontal>
                            {externalLinkButton}
                            {config.showAppTopBar ? (
                                <ClipFlyerItemButton
                                    class="flipp-button"
                                    classList={{
                                        secondary: externalLinkButton !== null,
                                    }}
                                    flyerId={item.flyerId}
                                    itemId={item.id}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div>
                        <FlyerItemElement itemId={item.id} />
                    </div>
                    <FlyerItemStructureData
                        flyerItemDetails={JSON.stringify(item)}
                    />
                </ItemDialog>
            </ToastContainer>
        </Dialog>
    ) as any as Dialog
}

export function makeCouponPop(coupon: Coupon, showterms = true) {
    const config = ConfigService.getInstance()
    let deepShareLink
    if (config.deepShareUrl) {
        deepShareLink = _makeDeepShareLink(
            config.deepShareUrl,
            coupon.id.toString()
        )
    }
    return (
        <Dialog
            class="coupon-pop"
            ref="dialog"
            id={`coupon-${coupon.id}-dialog`}
            transient
        >
            <DialogHeader>
                <div content-slot="title">{i18n('coupon_details_title')}</div>
            </DialogHeader>

            <ToastContainer>
                <CouponClippingDialogContent
                    couponId={coupon.id}
                    showterms={showterms}
                    deepShareLink={deepShareLink}
                />
            </ToastContainer>
        </Dialog>
    ) as any as Dialog
}

export function itemPopTitle(item: FlyerItemDetails, flyer: Flyer) {
    return item.displayType === DisplayType.LINK
        ? i18n('leaving_flipp')
        : i18n('item_details_title', {
              name: flyer.webIndexed
                  ? `${flyer.merchant} ${flyer.name}`
                  : item.name,
          })
}
export function renderDeleteAccountModal(returnPage: string) {
    const auth = AuthenticationService.getInstance()
    const locationService = LocationService.getInstance()

    const email = auth.user ? auth.user.info.email : ''

    let cssClass = locationService.locale.includes('fr') ? 'fr' : ''
    cssClass = email.length === 0 ? cssClass + ' redirect' : cssClass

    const dialog = (
        <Dialog
            class={`delete-account-popup ${cssClass}`}
            ref="dialog"
            transient
        >
            <div class="modal-content">
                <div class="header">
                    <h3>{i18n('delete_account_title')}</h3>
                    <button
                        class="close-button"
                        title={i18n('close_dialog_button')}
                        aria-label={i18n('close_dialog_button')}
                        onClick={handleKeep}
                    >
                        {icons.searchClose}
                    </button>
                </div>
                {email ? <p>{i18n('delete_account_text1')}</p> : null}

                {email ? <div class="email">{email}</div> : null}

                <p class="disclaimer-text">
                    <div>{i18n('delete_account_text2')}</div>
                </p>

                <div class="buttons-container">
                    <div class="buttons">
                        <button class="keep-button" onClick={handleKeep}>
                            {i18n('keep')}
                        </button>
                        {email ? (
                            <button
                                id="submit-delete-account-button"
                                class="submit-button"
                                onClick={() => handleSubmit(email)}
                            >
                                {i18n('delete_account_button')}
                            </button>
                        ) : (
                            <button
                                id="submit-delete-account-button"
                                class="redirect-button"
                                onClick={handleRedirect}
                            >
                                {i18n('delete_account_redirect')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div id="spinner-delete-account" class="loading">
                <div class="spinner"></div>
            </div>
        </Dialog>
    ) as any as Dialog

    dialog.addEventListener('flipp-navigate', () => {
        dialog.close()
    })
    dialog.showModal()
    async function handleSubmit(email: string) {
        try {
            const accountsService = AccountsService.getInstance()
            const analyticsService = AnalyticsService.getInstance()

            const configService = ConfigService.getInstance()
            const eventService = EventService.getInstance()

            const appVersionArray = configService.BUILD.split('/')
            const countryCodeArray = locationService.locale.split('-')

            const spinner = document.getElementById('spinner-delete-account')
            const deleteAccountButton = document.getElementById(
                'submit-delete-account-button'
            )

            spinner.classList.add('visible')
            deleteAccountButton.setAttribute('disabled', 'disabled')
            await accountsService.deleteUserData({
                email,
                user_id: auth.user.token['user_id'],
                access_token: auth.user.token['access_token'],
                sid: analyticsService.sessionId,
                fsa: locationService.postalCode,
                locale: locationService.locale,
                country_code:
                    countryCodeArray[countryCodeArray.length - 1].toUpperCase(),
                application_version:
                    appVersionArray[appVersionArray.length - 1],
            })
            spinner.classList.remove('visible')
            deleteAccountButton.removeAttribute('disabled')
            dialog.close()
            eventService.displayInfoPopup(
                DELETE_ACCOUNT_CONFIRMATION_POPUP_TAG_ELEMENT_ID
            )
            const provider = auth.user.info.provider
            const userGuid = auth.user.token.user_id
            auth.logout(this)
                .then(() => {
                    analyticsService.setAccountGuid(null)
                    eventService.fireAuthEvent('logout', null)
                    analyticsService.trackSignout(userGuid, provider)
                })
                .catch(() => {})
            eventService.unsubscribeFromRedirect()
        } catch (e) {
            const spinner = document.getElementById('spinner-delete-account')
            const deleteAccountButton = document.getElementById(
                'submit-delete-account-button'
            )
            spinner.classList.remove('visible')
            deleteAccountButton.removeAttribute('disabled')
            console.error(e)
        }
    }

    function handleKeep() {
        window.history.pushState({}, '', `/${returnPage}`)
        dialog.close()
    }

    function handleRedirect() {
        LinkService.getInstance().execute(`/signin#data-deletion`, this)
    }
}
